<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-water</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Flujo de caja</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Realizado en tesorería · proyección con vencimientos CxC / CxP
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Saldo actual</div>
          <div class="text-h5 font-weight-black primary--text">{{ summary.opening_balance | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Neto del mes</div>
          <div class="text-h5 font-weight-black">{{ summary.net_month | moneda }}</div>
          <div class="caption grey--text">In {{ summary.inflow_month | moneda }} · Out {{ summary.outflow_month | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Proyección 30 días</div>
          <div class="text-h5 font-weight-black">{{ summary.projected_balance_30 | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Proyección 90 días</div>
          <div class="text-h5 font-weight-black">{{ summary.projected_balance_90 | moneda }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-select
        v-model="accountId"
        :items="accountItems"
        item-text="text"
        item-value="value"
        outlined dense hide-details clearable
        label="Cuenta (vacío = consolidado)"
        class="modern-input"
        style="max-width: 360px;"
        @change="fetchAll"
      />
    </v-card>

    <v-card outlined class="modern-card mx-4 mb-6">
      <v-tabs v-model="tab" background-color="transparent">
        <v-tab>Línea de tiempo</v-tab>
        <v-tab>Vencimientos</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-data-table :headers="timelineHeaders" :items="timeline" :loading="loading" hide-default-footer class="elevation-0" no-data-text="Sin movimientos ni proyección.">
            <template v-slot:item.kind="{ item }">
              <v-chip x-small :color="item.kind === 'actual' ? 'primary' : 'warning'" class="white--text">
                {{ item.kind === 'actual' ? 'Realizado' : 'Proyectado' }}
              </v-chip>
            </template>
            <template v-slot:item.inflow="{ item }">{{ item.inflow | moneda }}</template>
            <template v-slot:item.outflow="{ item }">{{ item.outflow | moneda }}</template>
            <template v-slot:item.net="{ item }">{{ item.net | moneda }}</template>
            <template v-slot:item.balance="{ item }">
              <span class="font-weight-black">{{ item.balance | moneda }}</span>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <v-row>
            <v-col cols="12" md="6">
              <div class="pa-4 subtitle-2 font-weight-bold">Cobros esperados {{ due.inflow_total | moneda }}</div>
              <v-data-table :headers="dueHeaders" :items="due.inflows || []" hide-default-footer dense class="elevation-0">
                <template v-slot:item.amount="{ item }">{{ item.amount | moneda }}</template>
              </v-data-table>
            </v-col>
            <v-col cols="12" md="6">
              <div class="pa-4 subtitle-2 font-weight-bold">Pagos esperados {{ due.outflow_total | moneda }}</div>
              <v-data-table :headers="dueHeaders" :items="due.outflows || []" hide-default-footer dense class="elevation-0">
                <template v-slot:item.amount="{ item }">{{ item.amount | moneda }}</template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  fetchCashflowDueDetail,
  fetchCashflowSummary,
  fetchCashflowTimeline,
  fetchTreasuryAccounts,
} from '@/api/finance'

export default {
  name: 'FinanceCashflow',
  data() {
    return {
      tab: 0,
      loading: false,
      accountId: '',
      accounts: [],
      summary: {},
      timeline: [],
      due: {},
      timelineHeaders: [
        { text: 'Fecha', value: 'date' },
        { text: 'Tipo', value: 'kind' },
        { text: 'Ingresos', value: 'inflow', align: 'end' },
        { text: 'Egresos', value: 'outflow', align: 'end' },
        { text: 'Neto', value: 'net', align: 'end' },
        { text: 'Saldo', value: 'balance', align: 'end' },
      ],
      dueHeaders: [
        { text: 'Fecha', value: 'date' },
        { text: 'Origen', value: 'source_label' },
        { text: 'Contraparte', value: 'party_name' },
        { text: 'Ref.', value: 'reference' },
        { text: 'Monto', value: 'amount', align: 'end' },
      ],
    }
  },
  computed: {
    accountItems() {
      return this.accounts.map(acc => ({ value: acc.id, text: `${acc.code} · ${acc.name}` }))
    },
  },
  methods: {
    async fetchAll() {
      this.loading = true
      const params = { bank_account_id: this.accountId || undefined }
      try {
        const [summary, timeline, due, accounts] = await Promise.all([
          fetchCashflowSummary(params),
          fetchCashflowTimeline({ ...params, days_back: 14, days_forward: 60 }),
          fetchCashflowDueDetail({ days: 90, ...params }),
          fetchTreasuryAccounts(),
        ])
        this.summary = summary
        this.timeline = timeline.items || []
        this.due = due
        this.accounts = accounts.items || []
      } catch (e) {
        notifications.showError('No se pudo cargar el flujo de caja')
      } finally {
        this.loading = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
